import Joi from 'joi-full';
import get from 'lodash/get';
import { pipe } from 'ramda';
import { compose, withHandlers } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import isSafeInteger from 'lodash/isSafeInteger';

import { updateProductInventory } from '../actions';
import { syncValidateData } from '../../../common/validator';
import { showNotification } from '../../../common/helpers/notifications';
import { getBarberUser, getShop } from '../../../auth/selectors';

const negativeActions = ['damaged', 'returned', 'backbar'];
const positiveActions = ['newInventory', 'retail'];

const FORM = 'inventoryForm';

const mapStateToProps = state => ({
  quantity: get(state, `form[${FORM}].values.quantity`, 0),
  action: get(state, `form[${FORM}].values.action`),
  barberUser: getBarberUser(state),
  shop: getShop(state),
});

const mapDispatchToProps = {
  updateProductInventory,
};

const attributesToPayload = (currentInventory, barber) => (attrs) => {
  const payload = { ...attrs, quantity: attrs.quantity - currentInventory };

  if (barber) {
    payload.barberId = barber.id;
  }

  return payload;
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const props = { ...ownProps, ...stateProps, ...dispatchProps };

  props.initialValues = { quantity: props.product.inventory };
  const submit = props.updateProductInventory.bind(null, props.product.assignmentId, props.shop);
  props.onSubmit = pipe(attributesToPayload(props.product.inventory, props.barberUser), submit);

  return props;
};

const schema = Joi.object().keys({
  action: Joi.string().required(),
  quantity: Joi
    .label('New inventory count')
    .when('action', {
      is: Joi.valid(positiveActions),
      then: Joi.number().min(1).required(),
    })
    .when('action', {
      is: Joi.valid(negativeActions),
      then: Joi.number().min(Joi.ref('$currentInventory')).max(-1).required(),
      otherwise: Joi.number().required(),
    })
    .options({
      language: {
        number: {
          min: 'must be greater than current inventory count',
          max: 'must be less than current inventory count',
        },
      },
    }),
  description: Joi.string().when('action', {
    is: 'other',
    then: Joi.required(),
    otherwise: Joi.optional(),
  }),
});

const validate = (values, props) => {
  const context = { currentInventory: props.product.inventory * -1 };
  const formattedValues = isSafeInteger(values.quantity)
    ? { ...values, quantity: values.quantity - props.product.inventory }
    : values;

  return syncValidateData(formattedValues, schema, { context });
};

const form = reduxForm({
  form: FORM,
  onSubmitSuccess: (_values, _dispatch, props) => {
    props.close();
    showNotification({
      title: 'Done!',
      message: 'Inventory is updated',
      level: 'success',
    });
  },
  validate,
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps, mergeProps),
  form,
  withHandlers({
    // nested redux forms propagate submit event to the parent form :(
    // https://github.com/redux-form/redux-form/issues/3701
    stopPropagationAndSubmit: props => (e) => {
      e.preventDefault();
      e.stopPropagation();
      props.handleSubmit(props.onSubmit)(e);
    },
  }),
);

